import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";

export default function InstallPrompt() {
  const [prompt, setPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [installed, setInstalled] = useState(false);
  const { pathname } = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setInstalled(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      window.__ddInstallPrompt = e;
      window.dispatchEvent(new CustomEvent("dd-install-ready", { detail: e }));
      setPrompt(e);
      setTimeout(() => setShow(true), 3000);
    };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => { setInstalled(true); setShow(false); });

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") {
      setShow(false);
    }
    setPrompt(null);
  };

  if (installed || !show || pathname === "/welcome") return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-4 sm:max-w-sm"
        data-testid="install-prompt"
      >
        <div className="bg-card border border-border/50 rounded-2xl shadow-2xl p-4 backdrop-blur-xl">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-coral/10 flex items-center justify-center shrink-0">
              <Download size={18} className="text-coral" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-sm">{t("install.title")}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t("install.subtitle")}</p>
              <div className="flex items-center gap-2 mt-3">
                <Button
                  onClick={handleInstall}
                  size="sm"
                  className="rounded-full bg-coral hover:bg-coral-600 text-white text-xs gap-1.5"
                  data-testid="install-btn"
                >
                  <Download size={12} /> {t("install.btn")}
                </Button>
                <Button
                  onClick={() => setShow(false)}
                  variant="ghost"
                  size="sm"
                  className="rounded-full text-xs text-muted-foreground"
                >
                  {t("install.later")}
                </Button>
              </div>
            </div>
            <button onClick={() => setShow(false)} className="text-muted-foreground hover:text-foreground shrink-0">
              <X size={14} />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
